import React, { useEffect, useRef, useState } from 'react';

import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { ParameterMetadata, PrimitiveType } from '../../../../api/api';
import { JsonSchemaModel } from '../../Models/JsonSchemaModel';
import { convertJsonSchemaPropertyToEntityProperty } from '../../Services/EntityMetadataModelConverter';
import { getSchemaAlternatives, schemaAllowsNull } from '../../Services/JsonSchemaTypes';
import { matchesExpressionSchema, selectSchemaVariant } from '../../Services/SchemaExpressionTypes';
import { ExpressionPlaceholderProps } from './ExpressionPlaceholder';

type Props = ExpressionPlaceholderProps & {
	schema: JsonSchemaModel;
	renderValue: (props: ExpressionPlaceholderProps) => React.ReactNode;
};

export default function ExpressionSchemaPlaceholder({ schema, renderValue, ...props }: Props) {
	const { expression, onExpressionChange, parameterMetadata } = props;
	const alternatives = getSchemaAlternatives(schema).filter((branch) => branch.type !== 'null');
	const [selected, setSelected] = useState<number | undefined>();
	const drafts = useRef(new Map<number, typeof expression>());
	const schemaKey = JSON.stringify(schema);
	useEffect(() => {
		drafts.current.clear();
		setSelected(undefined);
	}, [schemaKey]);
	const [useExpression, setUseExpression] = useState(false);
	const [editing, setEditing] = useState(true);
	const matchingIndex = alternatives.findIndex((branch) => matchesExpressionSchema(branch, expression));
	const index = selected !== undefined && selected < alternatives.length ? selected : matchingIndex >= 0 ? matchingIndex : alternatives.length === 1 ? 0 : -1;
	const active = alternatives[index];
	const isNull = expression?.Type === 'Const' && expression.Value === null;
	const isDynamic = expression && !['Const', 'Object', 'Collection'].includes(expression.Type);
	const nullable = schemaAllowsNull(schema);
	const showUnset = !parameterMetadata.isRequired && !isDynamic && (expression || editing);
	const showControls = alternatives.length > 1 || nullable || isNull || showUnset;
	const choose = (next: number) => {
		if (index >= 0 && index !== next) drafts.current.set(index, expression);
		setSelected(next);
		setUseExpression(false);
		setEditing(true);
		onExpressionChange?.(drafts.current.has(next) ? drafts.current.get(next) : selectSchemaVariant(alternatives[next], expression));
	};
	const showEditor = !isNull && (parameterMetadata.isRequired || expression || editing);
	const property = active ? convertJsonSchemaPropertyToEntityProperty(parameterMetadata.name, active, !!parameterMetadata.isRequired) : undefined;
	const metadata: ParameterMetadata = {
		...parameterMetadata,
		expectedType: {
			...parameterMetadata.expectedType,
			type: !active?.type ? undefined : active.type === 'array' ? PrimitiveType.Collection : property?.type,
			typeJsonSchema: active,
			typeWrapInCollection: false,
		},
	};

	return (
		<Stack spacing={1} sx={{ minWidth: 0, width: '100%' }}>
			{showControls && <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
				{alternatives.length > 1 && (
					<TextField
						select
						size="small"
						label="Value type"
						value={index < 0 ? '' : index}
						sx={{ minWidth: 160 }}
						onChange={(event) => choose(Number(event.target.value))}
					>
						{alternatives.map((branch, i) => (
							<MenuItem key={i} value={i}>
								{branch.title ?? branch.type ?? `Variant ${i + 1}`}
							</MenuItem>
						))}
					</TextField>
				)}
				{nullable && !isNull && (
					<Button size="small" onClick={() => onExpressionChange?.({ Type: 'Const', Value: null })}>
						Set null
					</Button>
				)}
				{isNull && (
					<>
						<Typography variant="body2">Null</Typography>
						<Button size="small" onClick={() => (active ? choose(index) : onExpressionChange?.(undefined))}>
							Set value
						</Button>
					</>
				)}
				{showUnset && (
					<Button
						size="small"
						onClick={() => {
							setEditing(false);
							onExpressionChange?.(undefined);
						}}
					>
						Unset
					</Button>
				)}
				{alternatives.length > 1 && (
					<Button size="small" onClick={() => setUseExpression(!useExpression)}>
						{useExpression ? 'Edit value' : 'Use expression'}
					</Button>
				)}
			</Stack>}
			{useExpression || isDynamic
				? renderValue({
						...props,
						expectedObjectType: undefined,
						parameterMetadata: { ...parameterMetadata, expectedType: {} },
						allowedResultTypes: Array.from(
							new Set(
								alternatives.map((branch) =>
									branch.type === 'array' ? PrimitiveType.Collection : convertJsonSchemaPropertyToEntityProperty('', branch, false).type,
								),
							),
						),
					})
				: showEditor &&
					active &&
					('const' in active ? (
						<Button size="small" onClick={() => onExpressionChange?.({ Type: 'Const', Value: active.const })}>
							{String(active.const)}
						</Button>
					) : (
						renderValue({
							...props,
							parameterMetadata: metadata,
							expectedObjectType: active['x-allowsNull'] ? props.expectedObjectType : active.type === 'object' ? property?.referenceEntity : undefined,
							parameterEnum: property?.referenceEnum ?? props.parameterEnum,
						})
					))}
		</Stack>
	);
}
